import {
  DefaultError,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers';
import { invalidateIntegrationStatusRelatedQueries } from '@/modules/common';
import { ConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/configure-api-token-integration-params';
import { UseConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/use-configure-api-token-integration-params';

import {
  configureTempoIntegration,
  ConfigureTempoIntegrationRequest,
} from '../api/tempo-integration.api';

/**
 * Hook to handle Tempo integration configuration
 * Configures Tempo integration with API token and invalidates integration status on success
 */
export const useConfigureTempoIntegration = ({
  onSuccess,
  onError,
}: UseConfigureApiTokenIntegrationParams = {}): UseMutationResult<
  unknown,
  DefaultError,
  ConfigureApiTokenIntegrationParams
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  return useMutation({
    mutationFn: async (data: ConfigureTempoIntegrationRequest) => {
      await configureTempoIntegration(data);
    },
    onSuccess: async () => {
      // Invalidate integration status to refetch latest data
      await invalidateIntegrationStatusRelatedQueries(queryClient);

      // Show success toast
      displayToastSuccess(t('toast.integrationSuccess'));

      onSuccess?.();
    },
    onError: (error: Error) => {
      // Show error toast
      displayToastError(error.message);
      onError?.(error);
    },
  });
};
